import { useCallback, useContext } from 'react';

import AuthContext from '@/contexts/Auth/AuthContext';

interface HookProps {
  hash: string;
}

interface HookReturn {
  loadBinotel: () => void;
  triggerBinotelChat: () => Promise<void>;
}

const BINOTEL_SCRIPT_ID = 'binotel-chat-script';
let pendingOpen: Nullable<Promise<void>> = null;

export const useBinotel = ({ hash }: HookProps): HookReturn => {
  const { isLoading: isAuthContextLoading } = useContext(AuthContext);

  const waitForBinotel = useCallback(
    (): Promise<void> =>
      new Promise((resolve, reject) => {
        const startedAt = Date.now();

        const check = () => {
          const chat = Object.values(window.BinotelChat ?? {})[0];

          if (chat?.open) {
            chat.open();
            resolve();
            return;
          }

          if (Date.now() - startedAt > 10_000) {
            reject(new Error('Binotel widget not ready'));
            return;
          }

          setTimeout(check, 50);
        };

        check();
      }),
    [],
  );

  const loadBinotel = useCallback((): void => {
    const existingScript = document.getElementById(BINOTEL_SCRIPT_ID);

    if (isAuthContextLoading || !hash || existingScript) {
      return;
    }

    const script = document.createElement('script');

    script.id = BINOTEL_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widgets.binotel.com/chat/widgets/${hash}.js`;

    script.onerror = () => {
      console.error('Failed to load Binotel widget');
    };

    document.head.appendChild(script);
  }, [hash, isAuthContextLoading]);

  const triggerBinotelChat = useCallback(async () => {
    if (!document.getElementById(BINOTEL_SCRIPT_ID)) return;

    pendingOpen ??= waitForBinotel().finally(() => {
      pendingOpen = null;
    });

    await pendingOpen;
  }, [waitForBinotel]);

  return {
    loadBinotel,
    triggerBinotelChat,
  };
};
